<template>
  <div class="betSelectionContainer">
    <div class="header">
      <div class="title">{{ !isBetDone ? $t('flipCoinTooltip3') : $t('tavernChooseCoinSide') }}</div>
      <img class="close" src="/img/icons/bone_close.svg" alt="close" @click="$emit('close-bet-selection')" />
    </div>
    <div class="body">
      <template v-if="!isBetDone">
        <div class="owned">
          {{ $t('appStatisticOwnedToken', { tokenLabel: $t(token.label) }) }}:
          <img :src="getIcon(0, props.exchangeToken.tokenAddress, true)" alt="" />
          <span :class="hasLeastTokens ? '' : 'notEnoughAmount'">{{
            formatAmountFloor(props.exchangeToken.amount)
          }}</span>
        </div>
        <div class="betAmount">
          <el-input-number
            v-model="betAmount"
            :step="tokenConfig?.minAmount"
            :step-strictly="false"
            :validate-event="false"
            :min="tokenConfig?.minAmount || 100"
            :disabled="!props.exchangeToken.amount"
            :value-on-clear="props.exchangeToken.minAmount"
            :max="props.exchangeToken.maxAmount || 1000"
            @change="changeHandler"
          />
        </div>
        <div class="betButtons">
          <div v-for="item in betButtons" :key="item" class="betButton" @click="setAmount(item)">
            {{ typeof item === 'string' ? item : formatAmount(BigNumber(item).toNumber()) }}
          </div>
        </div>
        <TavernFlipCoinPointsForBet :points-for-bet="pointsForBet" :label-text="$t('tavernPointsForBet')" />
        <div class="placeBetBtnWrapper">
          <el-tooltip :disabled="hasLeastTokens" popper-class="popper-default" placement="bottom">
            <template #content>
              <div :class="{ 'tw:font-serif': $i18n.locale === 'ru' }">
                {{ $t('flipCoinNotEnoughTokensTooltip', { amount: props.exchangeToken.minAmount }) }}
              </div>
            </template>
            <div>
              <TavernFlipCoinBigButton
                class="placeBetBtn"
                :label="$t('tavernPlaceBet')"
                :disabled="!hasLeastTokens"
                @click="placeBetHandler"
              />
            </div>
          </el-tooltip>
        </div>
      </template>
      <template v-else>
        <div class="coinSideContainer">
          <div
            class="coinSide"
            :class="[selectedSide === CoinSide.Heads ? 'glow' : null]"
            @click="selectedSide = CoinSide.Heads"
          >
            <span>{{ $t('tavernCoinSideHeads') }}</span>
            <TavernFlipCoinIcon class="coinImg" :is-odd="true" />
          </div>
          <img
            v-if="selectedSide === CoinSide.Heads"
            class="green-check"
            src="/img/icons/green-check.svg"
            alt="green-check"
          />
        </div>
        <div class="coinSideContainer">
          <div
            class="coinSide"
            :class="[selectedSide === CoinSide.Tails ? 'glow' : null]"
            @click="selectedSide = CoinSide.Tails"
          >
            <span>{{ $t('tavernCoinSideTails') }}</span>
            <TavernFlipCoinIcon class="coinImg" :is-odd="false" />
          </div>
          <img
            v-if="selectedSide === CoinSide.Tails"
            class="green-check"
            src="/img/icons/green-check.svg"
            alt="green-check"
          />
        </div>
        <el-tooltip :disabled="selectedSide != null" placement="bottom" popper-class="popper-default">
          <div>
            <TavernFlipCoinBigButton
              :disabled="selectedSide == null"
              class="continueBtn"
              :label="$t('coreContinue')"
              @click="placeOddHandler"
            />
          </div>

          <template #content>
            <div class="tooltipNotSelected" :class="{ 'ru-locale': $i18n.locale === 'ru' }">
              {{ $t('flipCoinTooltipNotSelected') }}
            </div>
          </template>
        </el-tooltip>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IFlipCoinTokenConfig } from '~/types/apiService';
import { tokenMetaData } from '~/utils';
import { formatAmountFloor } from '../../../utils/formatters';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import BigNumber from 'bignumber.js';
import { parseUnits } from 'ethers';

enum CoinSide {
  Heads = 'Heads',
  Tails = 'Tails'
}

const props = defineProps<{
  exchangeToken: IFlipCoinTokenConfig;
}>();

const { blockchain } = useEnvs();
const emit = defineEmits(['set-token-game-data', 'set-odd', 'close-bet-selection']);
const { address } = useWeb3ModalAccount();
const { getLastAddedSPTimeAndMaxSP } = useFlipCoinMethods();
const { data: tokensList } = useNuxtData<IFlipCoinTokenConfig[]>('tokensList');

const selectedSide = ref<CoinSide | null>(null);
const isBetDone = ref(false);
const betAmount = ref(props.exchangeToken.minAmount);
const betButtons = ref([
  BigNumber(props.exchangeToken.minAmount).toNumber(),
  BigNumber(props.exchangeToken.minAmount).multipliedBy(2.5).toNumber(),
  BigNumber(props.exchangeToken.minAmount).multipliedBy(5).toNumber(),
  BigNumber(props.exchangeToken.minAmount).multipliedBy(10).toNumber(),
  'Max'
]);
const token = computed(() => tokenMetaData(blockchain.contracts, props.exchangeToken.tokenAddress));
const tokenConfig = computed(() => tokensList.value?.find((token) => token.id === props.exchangeToken.id));
const hasLeastTokens = computed(() => {
  return props.exchangeToken.amount >= props.exchangeToken.minAmount;
});

const lastSPInCurrentPeriod = asyncComputed(
  async () =>
    props.exchangeToken.tokenAddress ? await getLastAddedSPTimeAndMaxSP(props.exchangeToken.tokenAddress) : [0, 0, 0],
  [0, 0, 0]
);

const pointsForBet = computed(() => {
  const totalAmountOfBetsInCurrentPeriod = BigNumber(parseUnits(betAmount.value?.toString() || '0', 'ether').toString())
    .plus(lastSPInCurrentPeriod.value[0].toString())
    .toNumber();

  return getSPAmount(
    totalAmountOfBetsInCurrentPeriod,
    props.exchangeToken.seasonPointsMultiplier,
    parseInt(lastSPInCurrentPeriod.value[2].toString())
  );
});

watch(
  address,
  () => {
    emit('close-bet-selection');
  },
  { immediate: false }
);

const changeHandler = (amount: number | undefined) => {
  if (!amount) return props.exchangeToken.minAmount;

  if (amount > props.exchangeToken.amount) {
    betAmount.value = Math.floor(props.exchangeToken.amount);
  } else if (props.exchangeToken.amount < amount) {
    betAmount.value = props.exchangeToken.minAmount;
  } else {
    betAmount.value = amount;
  }
};

const setAmount = (btnAmount: number | string) => {
  if (typeof btnAmount === 'string') {
    betAmount.value =
      props.exchangeToken.amount >= props.exchangeToken.maxAmount
        ? props.exchangeToken.maxAmount
        : props.exchangeToken.amount;
  } else {
    betAmount.value = props.exchangeToken.amount >= btnAmount ? btnAmount : props.exchangeToken.amount;
  }
};

const placeBetHandler = () => {
  emit('set-token-game-data', { token: props.exchangeToken.tokenAddress, playAmount: betAmount.value });
  isBetDone.value = true;
};

const placeOddHandler = () => {
  emit('set-odd', selectedSide.value === CoinSide.Heads ? true : false);
};
</script>

<style lang="scss">
.tooltipBetSelection {
  span {
    color: #88e1ff;
  }
}

.tooltipNotSelected {
  font-size: 20px;
  line-height: 24px;
  max-width: 300px;
  padding: 15px 6px;
  text-align: center;
}

.el-input-number__decrease,
.el-input-number__increase {
  border: none;
  top: 0;
}

.el-input-number__increase {
  border: none;
  right: 0;
}

.el-input-number__decrease {
  border: none;
  left: 0;
}

.el-input:focus-within {
  --el-input-focus-border-color: transparent;
  --el-input-focus-border: transparent;
}
</style>

<style lang="scss" scoped>
.betSelectionContainer {
  width: 461px;
  border-radius: 12px;
  background: linear-gradient(to bottom, #4e3127, #000000);
  margin: 0 auto;
  margin-top: 40px;
  padding-bottom: 16px;
  font-weight: 700;
  border: 1px solid #483b20;
  font-family: Eczar, sans-serif;

  @media screen and (max-width: 479px) {
    width: 99vw;
    margin-top: 0;
  }
}

.header {
  text-align: center;
  margin: 33px 0 19px;
  position: relative;

  .title {
    font-size: 32px;
    line-height: 32px;
    color: #fff;

    font-family: Grenze, sans-serif;
    font-weight: 700;
    text-align: center;
  }

  .close {
    width: 20px;
    height: 20px;
    position: absolute;
    right: 20px;
    top: 8px;
    cursor: pointer;
  }
}

.body {
  text-align: center;
  font-size: 20px;
  color: #cfd3dc;

  .el-tooltip__trigger {
    max-width: fit-content;
    margin: 0 auto;
    height: fit-content;
  }

  .betAmount {
    &:deep(.el-input__wrapper) {
      background-color: #07000080;
      color: #fff;
      --el-input-focus-border-color: transparent;
      --el-input-focus-border: transparent;
      box-shadow: none;
    }

    .el-input-number {
      height: 45px;
      width: fit-content;
      margin-top: 15px;
      border: 2px solid #fff;
      border-radius: 12px;
      overflow: hidden;

      ::v-deep {
        .el-input-number__decrease,
        .el-input-number__increase {
          background: linear-gradient(to bottom, #e29705, #702a01);
          height: 45px;
        }

        .el-input__inner {
          font-size: 24px;
          font-weight: 700;
          font-family: Eczar;
          color: #fff;
        }

        .el-icon {
          fill: #fff;
          color: #fff;
        }
      }
    }
  }

  .owned {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 500;

    span {
      color: #fff;
    }

    .notEnoughAmount {
      color: red;
    }

    img {
      width: 40px;
      height: 40px;
      margin: 0px 4px;
    }
  }

  .minimumBet {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 500;

    span {
      color: #fff;
    }

    .title {
      color: #a2a2a2;
    }

    img {
      width: 30px;
      height: 30px;
      margin: 0px 4px;
    }
  }

  .betButtons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 70%;
    margin: 0 auto;
    margin-bottom: 20px;
    margin-top: 11px;

    @media screen and (max-width: 479px) {
      gap: 10px;
      width: 95%;
    }

    .betButton {
      border: 1px solid #a2a2a2;
      border-radius: 8px;
      width: 57px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #fff;
      font-size: 18px;
      font-family: Eczar;
      font-weight: 600;
    }
  }

  .placeBetBtnWrapper {
    margin: 0 auto;
    margin-top: 17px;

    .el-tooltip__trigger {
      margin: 0 auto;
    }

    .placeBetBtn {
      width: 100%;
    }
  }

  .coinSideContainer {
    position: relative;
    display: flex;
    align-items: center;
    width: fit-content;
    margin: 0 auto;
    margin-top: 45px;

    .green-check {
      width: 35px;
      height: 35px;
      margin-left: 25px;
      position: absolute;
      right: -60px;
    }
  }

  .coinSide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 226px;
    height: 48px;
    border: 1px solid #483b20;
    color: #fff;
    border-radius: 8px;
    background-color: #07000080;
    position: relative;
    font-weight: 600;
    &:not(.glow) {
      cursor: pointer;
    }

    span {
      padding-right: 20px;
    }

    .coinImg {
      position: absolute;
      right: -24px;
      width: 74px;
      height: 74px;
    }
  }

  .glow {
    box-shadow: 0 0 6px 2px rgba(255, 165, 0, 0.8);
  }

  .continueBtn {
    margin-top: 30px;
    height: 62px;
    border-width: 2px;
  }
}
</style>
